// ============================================================================
// JARVISTRAVEL - PASSWORD RULES, TAKEN FROM CLERK (JAR-1702)
//
// Every password this app asks for is judged by Clerk, so the rule the form
// applies has to BE Clerk's rule. It was a copy we invented: 8 characters plus
// an uppercase, a lowercase and a number. Production Clerk asks for 15
// characters and no composition at all, and scores strength and breaches
// itself. So a 10-character password passed our form and was refused by Clerk
// at the end, and a long all-lowercase passphrase Clerk would accept was
// refused by ours up front.
//
// Read from the instance's own environment document, the same one Clerk's SDK
// reads. Not `__unstable__environment`: the name says what it is, and a rename in
// a minor version would take the sign-up screen with it.
//
// WHEN THE RULES CANNOT BE READ, INVENT NOTHING. No local rule, no displayed
// requirement: the submit goes through and Clerk's own answer is shown on the
// field. A local rule guessing at Clerk's is the bug this module exists to end.
// ============================================================================

import { useEffect, useState } from 'react';

export interface PasswordRules {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChar: boolean;
  /** Clerk scores strength itself; the form can only say that it will. */
  checksStrength: boolean;
  /** Clerk checks known breaches itself; the form can only say that it will. */
  checksBreaches: boolean;
}

// One read per page load, shared by every screen that asks. A failed read is
// forgotten, so the next screen to ask tries again rather than inheriting it.
let pending: Promise<PasswordRules | null> | null = null;

/** Test seam: forget the shared read. */
export function __resetPasswordRules(): void {
  pending = null;
}

type EnvironmentDocument = {
  user_settings?: {
    password_settings?: {
      min_length?: unknown;
      require_uppercase?: unknown;
      require_lowercase?: unknown;
      require_numbers?: unknown;
      require_special_char?: unknown;
      min_zxcvbn_strength?: unknown;
      disable_hibp?: unknown;
    };
  };
};

/** What the form needs from the environment document, or null if it is not there. */
export function parsePasswordRules(doc: unknown): PasswordRules | null {
  const s = (doc as EnvironmentDocument | null)?.user_settings?.password_settings;
  // No minimum is no rule we can trust: say nothing rather than guess one.
  if (!s || typeof s.min_length !== 'number') return null;
  return {
    minLength: s.min_length,
    requireUppercase: s.require_uppercase === true,
    requireLowercase: s.require_lowercase === true,
    requireNumbers: s.require_numbers === true,
    requireSpecialChar: s.require_special_char === true,
    checksStrength: typeof s.min_zxcvbn_strength === 'number' && s.min_zxcvbn_strength > 0,
    checksBreaches: s.disable_hibp !== true,
  };
}

/**
 * Clerk's frontend API host, decoded from the publishable key the app already
 * ships (`pk_live_` or `pk_test_`, then the host plus a trailing `$`, base64).
 *
 * From the key rather than from `useClerk().frontendApi`, so this does not depend
 * on a Clerk hook: every test file that mocks @clerk/clerk-react would otherwise
 * have to list one more export or throw on render.
 *
 * Keys are issued without `=` padding. `atob` is fine with that (the WHATWG
 * forgiving-base64 decode, measured on every length a host can produce); a
 * strict decoder is not, and truncated this very host to `clerk.jarvistravel.co`
 * once in a Python check. So if this ever moves off `atob`, the decoder must
 * forgive padding too; a test pins a real-shaped unpadded key.
 *
 * The result must look like a host, or nothing is fetched.
 */
export function frontendApiFromPublishableKey(key: string | undefined): string | null {
  const match = /^pk_(?:live|test)_(.+)$/.exec(key ?? '');
  if (!match) return null;
  try {
    const host = atob(match[1]).replace(/\$$/, '');
    return /^[a-z0-9.-]+$/i.test(host) ? host : null;
  } catch {
    return null;
  }
}

export function loadPasswordRules(frontendApi: string | null | undefined): Promise<PasswordRules | null> {
  if (!frontendApi) return Promise.resolve(null);
  if (pending) return pending;
  const read = fetch(`https://${frontendApi}/v1/environment`)
    .then((res) => (res.ok ? res.json() : null))
    .then(parsePasswordRules)
    .catch(() => null)
    .then((rules) => {
      if (rules === null) pending = null;
      return rules;
    });
  pending = read;
  return read;
}

/** Clerk's password rules for this instance, or null while unknown or unreadable. */
export function usePasswordRules(): PasswordRules | null {
  const frontendApi = frontendApiFromPublishableKey(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY);
  const [rules, setRules] = useState<PasswordRules | null>(null);
  useEffect(() => {
    let live = true;
    void loadPasswordRules(frontendApi).then((r) => {
      if (live) setRules(r);
    });
    return () => {
      live = false;
    };
  }, [frontendApi]);
  return rules;
}

/**
 * The first rule this password breaks, or null.
 *
 * Only what can be checked HERE: length and composition. Strength and breaches
 * are Clerk's to judge, and scoring them locally would mean the library this
 * ticket declined. With no rules, nothing is checked.
 */
export function checkPassword(password: string, rules: PasswordRules | null): string | null {
  if (!rules) return null;
  if (password.length < rules.minLength) return `Use at least ${rules.minLength} characters.`;
  if (rules.requireUppercase && !/[A-Z]/.test(password)) return 'Include an uppercase letter.';
  if (rules.requireLowercase && !/[a-z]/.test(password)) return 'Include a lowercase letter.';
  if (rules.requireNumbers && !/\d/.test(password)) return 'Include a number.';
  if (rules.requireSpecialChar && !/[^A-Za-z0-9]/.test(password)) return 'Include a symbol.';
  return null;
}

/** The requirements to show under the field, or undefined when they are unknown. */
export function describePasswordRules(rules: PasswordRules | null): string | undefined {
  if (!rules) return undefined;
  const shape = [
    rules.requireUppercase && 'an uppercase letter',
    rules.requireLowercase && 'a lowercase letter',
    rules.requireNumbers && 'a number',
    rules.requireSpecialChar && 'a symbol',
  ].filter(Boolean);
  const first = shape.length
    ? `Use at least ${rules.minLength} characters, including ${shape.join(', ')}.`
    : `Use at least ${rules.minLength} characters. A long phrase is easiest to remember.`;
  const judged = [
    rules.checksStrength && 'too easy to guess',
    rules.checksBreaches && 'found in a known data breach',
  ].filter(Boolean);
  return judged.length ? `${first} It can't be ${judged.join(' or ')}.` : first;
}
